import { useEffect } from "react";
import "./classic.css";

// Webshop-thema's. Een thema bestaat uit:
//  - een id die op <html data-shop-theme="…"> komt te staan;
//  - optioneel extra Google Fonts;
//  - een CSS-bestand in deze map dat de thema-tokens (kleuren, fonts, afrondingen)
//    en de paar structurele verschillen (kaders, koppen) invult.
// Componenten gebruiken enkel de tokens (`rounded-panel`, `bg-accent`, `text-primary`…)
// en de `shop-*`-haakjes; ze weten zelf niet welk thema actief is.
//
// Nieuw thema: voeg het hier toe, maak <id>.css aan en zet de id ook in
// DishOnline-backend/config/shopThemes.js.
export const SHOP_THEMES = [
   {
      id: "modern",
      label: "Modern",
      description: "Licht en rustig, afgeronde kaarten, zwart-wit. Je merkkleur wordt niet gebruikt.",
   },
   {
      id: "classic",
      label: "Klassiek kader",
      description: "Als de menukaart van een Franse brasserie: ivoor papier, een fijne serif, kaders en accenten in je merkkleur.",
      fontsUrl:
         "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;0,700;1,400;1,500&family=Jost:wght@400;500;600&display=swap",
   },
];

export const DEFAULT_SHOP_THEME = SHOP_THEMES[0];

export const getShopTheme = (id) => SHOP_THEMES.find((theme) => theme.id === id) || DEFAULT_SHOP_THEME;

const isHex = (value) => /^#[0-9a-f]{6}$/i.test(value || "");

// Zwarte of witte tekst op de merkkleur, wat het best leesbaar is (WCAG-luminantie).
export const readableTextOn = (hex) => {
   const channel = (offset) => {
      const c = parseInt(hex.slice(offset, offset + 2), 16) / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
   };
   const luminance = 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
   return luminance > 0.4 ? "#111111" : "#ffffff";
};

// Zet thema, merkkleur en donkere modus van de shop op <html>, en ruimt alles op
// wanneer je de shop verlaat (het dashboard deelt dezelfde pagina).
export function useShopTheme(shop) {
   const theme = getShopTheme(shop?.shopTheme);
   const brand = isHex(shop?.brandingColor) ? shop.brandingColor : "#1d1d1f";
   const darkMode = !!shop?.darkMode;

   useEffect(() => {
      const root = document.documentElement;
      root.dataset.shopTheme = theme.id;
      root.style.setProperty("--brand", brand);
      root.style.setProperty("--brand-text", readableTextOn(brand));
      root.classList.toggle("dark-mode", darkMode);

      let fonts;
      if (theme.fontsUrl) {
         fonts = document.createElement("link");
         fonts.rel = "stylesheet";
         fonts.href = theme.fontsUrl;
         document.head.appendChild(fonts);
      }

      return () => {
         delete root.dataset.shopTheme;
         root.style.removeProperty("--brand");
         root.style.removeProperty("--brand-text");
         root.classList.remove("dark-mode");
         fonts?.remove();
      };
   }, [theme.id, theme.fontsUrl, brand, darkMode]);

   return theme;
}
